import {
  getRequiredFields,
} from "@/lib/utils";
import { CATEGORY, ID, LAG_DAYS, SUB_CATEGORY, TASK_NAME, TYPE } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useForm, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { useGetTaskCategoriesByProject, useGetTaskListByCategory, useGetTaskSubCategoriesByCategoryId } from "@/services";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { routes } from "@/routes/utils";

const useAddEdit = ({setOpen, onSaveDeps, isEdit}: {setOpen: any, onSaveDeps: (val: any) => void, isEdit?: boolean}) => {
  const {id, planId, action, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const [keys, setKeys] = useState<any>({
    category: Math.random() * 2,
    subcategory: Math.random() * 2,
    task: Math.random() * 2,
  });

  const form = useForm<formValues>({
    defaultValues: {
      [ID]: "", [TASK_NAME]: "", [TYPE]: "", [LAG_DAYS]: "0", [CATEGORY]: "", [SUB_CATEGORY]: ""
    },
    resolver: zodResolver(schema),
  });
  
  const {taskData, dependencyData} = useSelector((state: RootState) => state.currentData);

  const requiredFields = getRequiredFields(schema);

  const { data: categoryList, isLoading: isCategoryListLoading } =
    useGetTaskCategoriesByProject({ projectId: id as string });

  const { data: subCategoryList, refetch: refetchTaskSubCategories, isRefetching: isRefetchingSubCategories } =
    useGetTaskSubCategoriesByCategoryId({ categoryId: form.watch(CATEGORY) as string, queryParams: {
      enabled: false
    } });
    
  const { data: taskList, refetch: refetchTaskList, isRefetching: isRefetchingTaskList } =
  useGetTaskListByCategory({ 
    projectId: id as string,
    planId: planId as string,
    categoryId: form.watch(CATEGORY) as string,
    subCategoryId: form.watch(SUB_CATEGORY) as string,
    queryParams: {
      enabled: false
    } 
  });
    
  const category = useWatch({control: form.control, name: CATEGORY });
  const subcategory = useWatch({control: form.control, name: SUB_CATEGORY });

  useEffect(() => {
    if (category) {
      refetchTaskSubCategories()
    }
  }, [category]);

  useEffect(() => {
    if (subcategory) {
      refetchTaskList()
    }
  }, [subcategory]);
  
  useEffect(() => {
    if (dependencyData && isEdit) {
      form.reset({
        [ID]: dependencyData?.[ID]?.toString() || "",
        [TASK_NAME]: dependencyData?.[TASK_NAME]?.toString() || "",
        [TYPE]: dependencyData?.[TYPE]?.toString() || "",
        [LAG_DAYS]: dependencyData?.[LAG_DAYS]?.toString() || "0",
        [CATEGORY]: dependencyData?.dependsOnTaskCategoryId?.toString() || "",
        [SUB_CATEGORY]: dependencyData?.dependsOnTaskSubCategoryId?.toString() || ""
      })
      setKeys((prev: any) => ({
        ...prev,
        category: Math.random() * 2,
        subcategory: Math.random() * 2,
      }))
    }
  }, [dependencyData, isEdit]);


  useEffect(() => {
    if (categoryList?.data) {
      setKeys((prev: any) => ({
        ...prev,
        category: Math.random() * 2,
      }));
    }
  }, [categoryList]);

  useEffect(() => {
    if (subCategoryList?.data) {
      setKeys((prev: any) => ({
        ...prev,
        subcategory: Math.random() * 2,
      }))
    }
  }, [subCategoryList]);

  useEffect(() => {
    if (taskList?.data) {
      setKeys((prev: any) => ({
        ...prev,
        task: Math.random() * 2,
      }))
    }
  }, [taskList]);

  const onSubmit = (data: formValues) => {
    const selectedCategory = categoryList?.data?.find((val: any) => val?.taskCategoryId == data?.dependsOnTaskCategoryId);
    const selectedSubCategory = subCategoryList?.data?.find((val: any) => val?.taskSubCategoryId == data?.dependsOnTaskSubCategoryId);

    onSaveDeps({...data,
      dependsOnTaskCategoryName: selectedCategory?.title,
      dependsOnTaskSubCategoryName: selectedSubCategory?.title
    });

    onCancel();
  }

  const onCancel = () => {
    form.reset({
      [ID]: "", [TASK_NAME]: "", [TYPE]: "", [LAG_DAYS]: "0", [CATEGORY]: "", [SUB_CATEGORY]: ""
    })
    setOpen(false);
  }

  return {
    form,
    requiredFields,
    taskList,
    taskData,
    isView,
    onSubmit,
    isCategoryListLoading,
    isRefetchingSubCategories,
    categoryList,
    subCategoryList,
    isRefetchingTaskList,
    onCancel,
    keys
  };
}

export default useAddEdit;